/**
 * @file PriceSuggestions.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-10-01
 * @description The suggested sale price for one line, and the menu behind it:
 *              what this customer was charged before (orders and invoices),
 *              their contracts, and — on a Direct Delivery line — the haulage
 *              folded in. Picking one hands the price to `onApply`.
 *
 *              Moved out of Price Orders so Create Invoice (from a customer
 *              order) offers exactly the same suggestions. The data is one
 *              line's entry from GET /api/orders/customer-orders/:id/suggested-prices.
 *
 *              With nothing to suggest, `onRequestPrice` (when given) turns the
 *              cell into the "email the rep for a price" button, as on Price Orders.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { formatPrice } from '../utils/currencyFormatter';
import './PriceSuggestions.css';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Where a Direct Delivery line's haulage comes from, in a sentence. */
export const haulageExplanation = (h) => {
  if (!h) return '';
  const source = h.overridden ? 'typed on the order'
    : h.prefix ? `${h.prefix} rate${h.effective_from ? ` from ${formatDate(h.effective_from)}` : ''}` : 'supplier rate';
  if (h.pallets == null || h.per_unit == null) {
    return `Haulage ${formatPrice(h.rate)}/pallet (${source}) — pallets not known, so not included. Set the pallets on the order.`;
  }
  return `Haulage: ${h.pallets} pallet${h.pallets === 1 ? '' : 's'} × ${formatPrice(h.rate)} = ${formatPrice(h.total)} `
    + `→ +${formatPrice(h.per_unit)} per unit (${source})`;
};

const sourceLabel = (s) => ({ SO: 'Sales Order', INV: 'Invoice', PO: 'Purchase Order' }[s] || s);
const additiveNames = (list) => (list || [])
  .map(a => a.name || a.additive_name || a.code || a.additive_code)
  .filter(Boolean)
  .join(' + ');
const fmtMargin = (c) => {
  if (c.margin == null) return '';
  if (c.margin_kind === 'fixed_amount') return ` + £${Number(c.margin).toFixed(2)} margin`;
  return ` + ${Number(c.margin).toFixed(2)}% margin`;
};

/*
 * Under a suggestion on a Direct Delivery line: where its haulage came from.
 * "Added" and "unknown" show the plain price so the addition is visible; an
 * unknown source (a Xero invoice, with no terms to tell by) also offers the
 * plain price on its own, in case it already had haulage in it.
 */
function HaulageNote({ entry, haulage, apply }) {
  if (!entry.haulage_status || !haulage) return null;
  if (entry.haulage_status === 'included') {
    return <span className="suggestion-haulage included" title="This price was for a Direct Delivery, so haulage is already in it">· includes haulage</span>;
  }
  const plain = formatPrice(entry.price || 0);
  const add = formatPrice(haulage.per_unit || 0);
  if (entry.haulage_status === 'added') {
    return <span className="suggestion-haulage added">· {plain} + {add} haulage</span>;
  }
  return (
    <span className="suggestion-haulage unknown" title="No delivery terms on record for this price, so it may already include haulage">
      · {plain} + {add} haulage — may already include it{' '}
      <span
        role="button"
        tabIndex={0}
        className="suggestion-haulage-plain"
        onClick={(e) => { e.stopPropagation(); apply(entry.price); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); apply(entry.price); } }}
      >
        use {plain}
      </span>
    </span>
  );
}

/**
 * @param {object}   suggestion     one line's suggested-prices entry (or undefined)
 * @param {function} onApply        (price) => void
 * @param {function} [onRequestPrice] () => void — the "email the rep" action
 * @param {boolean}  [disabled]
 */
function PriceSuggestions({ suggestion: sug, onApply, onRequestPrice, disabled = false }) {
  // anchor = the trigger's viewport rect (below = just under it, above = its
  // top) so the menu can flip up when there is no room below.
  const [anchor, setAnchor] = useState(null);
  const [top, setTop] = useState(null);
  const popRef = useRef(null);
  const triggerRef = useRef(null);
  const close = () => { setAnchor(null); setTop(null); };

  // Measure once mounted: below the trigger, or flipped above when it would
  // overflow the viewport. Before paint, so there is no visible jump.
  useLayoutEffect(() => {
    if (!anchor || !popRef.current) return;
    const h = popRef.current.offsetHeight;
    const margin = 8;
    if (anchor.below + h <= window.innerHeight - margin) setTop(anchor.below);
    else if (anchor.above - h - 4 >= margin) setTop(anchor.above - h - 4);
    else setTop(Math.max(margin, window.innerHeight - h - margin));
  }, [anchor]);

  // Closes on a click elsewhere, Escape, or any scroll (which would leave it
  // floating away from its row).
  useEffect(() => {
    if (!anchor) return undefined;
    const onDown = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onScroll = (e) => { if (!(popRef.current && popRef.current.contains(e.target))) close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [anchor]);

  if (!sug) return <span className="no-suggestion">-</span>;
  const prior = sug.previously_sold || [];
  const contracts = sug.contracts || [];
  const np = sug.no_price || null;

  // Nothing on record: the rep is who knows the price.
  if (prior.length + contracts.length === 0) {
    if (!np || !onRequestPrice) return <span className="no-suggestion">-</span>;
    const requested = !!np.price_requested_at;
    return (
      <button
        type="button"
        className={`request-price-btn${requested ? ' requested' : ''}`}
        onClick={onRequestPrice}
        disabled={disabled}
        title={requested
          ? `Price requested by ${np.price_requested_by} on ${formatDate(np.price_requested_at)}`
          : (np.rep_name ? `Email ${np.rep_name} for price` : 'Request price via email')}
      >
        <span className="material-symbols-rounded">mail</span>
      </button>
    );
  }

  // The trigger shows the top suggestion (most recent charge, else the most
  // recent contract), with haulage folded in on a Direct Delivery line.
  const first = prior[0] || contracts[0];
  const firstPrice = first.price_with_haulage ?? first.price;
  const apply = (price) => { onApply(price); close(); };
  const lineSig = sug.additive_signature || '';
  const lineAdditives = sug.line_additives || [];

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="suggested-price-btn suggestion-trigger"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (anchor) { close(); return; }
          const r = e.currentTarget.getBoundingClientRect();
          setTop(null);
          setAnchor({ below: r.bottom + 4, above: r.top, left: r.left });
        }}
        title={sug.haulage?.per_unit != null
          ? `Click for suggestions — ${haulageExplanation(sug.haulage)}`
          : 'Click for suggestions'}
      >
        {first.haulage_status && first.haulage_status !== 'included' && (
          <span className="material-symbols-rounded suggestion-haulage-icon">local_shipping</span>
        )}
        {formatPrice(firstPrice || 0)}
        <span className="material-symbols-rounded suggestion-caret">arrow_drop_down</span>
      </button>

      {anchor && ReactDOM.createPortal(
        <div
          ref={popRef}
          className="suggestion-popover"
          style={{
            position: 'fixed',
            top: top != null ? top : anchor.below,
            left: anchor.left,
            // Hidden until measured, so it never flashes in the wrong place.
            visibility: top != null ? 'visible' : 'hidden',
            zIndex: 2000
          }}
        >
          {lineAdditives.length > 0 && (
            <div className="suggestion-additives-header" title="Additive recipe on this order line">
              <span className="material-symbols-rounded">science</span>
              <span>{additiveNames(lineAdditives)}</span>
            </div>
          )}
          {sug.haulage && (
            <div className={`suggestion-haulage-header${sug.haulage.per_unit == null ? ' unknown' : ''}`}>
              <span className="material-symbols-rounded">local_shipping</span>
              <span>
                {haulageExplanation(sug.haulage)}
                {sug.haulage.per_unit != null && '. Added to the prices below unless they already include it.'}
              </span>
            </div>
          )}
          {prior.length > 0 && (
            <div className="suggestion-section">
              <div className="suggestion-section-title">Previously charged</div>
              {prior.map((p, idx) => {
                const recipeDiffers = (p.additive_signature || '') !== lineSig;
                const priorAdditives = additiveNames(p.additives);
                return (
                  <button type="button" key={`p${idx}`} className="suggestion-row" onClick={() => apply(p.price_with_haulage ?? p.price)}>
                    <span className={`suggestion-source-tag src-${String(p.source || '').toLowerCase()}`}>{p.source}</span>
                    <span className="suggestion-row-price">{formatPrice((p.price_with_haulage ?? p.price) || 0)}</span>
                    <span className="suggestion-row-context">
                      {p.doc_number || sourceLabel(p.source)}{p.date ? ` · ${formatDate(p.date)}` : ''}
                      {recipeDiffers && (
                        <span className="recipe-mismatch" title="Different additive recipe">
                          · {priorAdditives || 'no additives'}
                        </span>
                      )}
                      <HaulageNote entry={p} haulage={sug.haulage} apply={apply} />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {contracts.length > 0 && (
            <div className="suggestion-section">
              <div className="suggestion-section-title">Contracts</div>
              {contracts.map((c, idx) => (
                <button type="button" key={`c${idx}`} className="suggestion-row" onClick={() => apply(c.price_with_haulage ?? c.price)}>
                  <span className="suggestion-source-tag src-contract">CONTRACT</span>
                  <span className="suggestion-row-price">{formatPrice((c.price_with_haulage ?? c.price) || 0)}</span>
                  <span className="suggestion-row-context">
                    {c.period_name || (c.start_date && c.end_date ? `${formatDate(c.start_date)} – ${formatDate(c.end_date)}` : 'Period n/a')}
                    {c.cost != null ? ` · cost ${formatPrice(c.cost)}` : ''}
                    {fmtMargin(c)}
                    {c.contract_number ? ` · ${c.contract_number}` : ''}
                    <HaulageNote entry={c} haulage={sug.haulage} apply={apply} />
                  </span>
                </button>
              ))}
            </div>
          )}
          {np && onRequestPrice && (
            <div className="suggestion-section suggestion-section-footer">
              <button type="button" className="suggestion-row email-rep" onClick={() => { onRequestPrice(); close(); }}>
                <span className="material-symbols-rounded">mail</span>
                <span className="suggestion-row-context">
                  {np.price_requested_at
                    ? `Price requested by ${np.price_requested_by} on ${formatDate(np.price_requested_at)}`
                    : (np.rep_name ? `Email ${np.rep_name} for a price` : 'Request a price via email')}
                </span>
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

export default PriceSuggestions;
